import { OnboardingDetailsRole } from '@nemuru-eng/js-api-client';
import { useState } from 'react';

import {
  PaymentMethodEditAction,
  PaymentMethodEditActionsDialog,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Header/CheckoutSessionActions/components/PaymentMethodEditActionsDialog';
import { usePaymentMethodEditActions } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Header/CheckoutSessionActions/hooks/usePaymentMethodEditActions';
import { AutoLoanPaymentMethodAction } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Header/CheckoutSessionActions/types/AutoLoanPaymentMethodAction';
import { LoanPaymentMethodAction } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Header/CheckoutSessionActions/types/LoanPaymentMethodAction';
import { Font } from '@/components/shared/components/Font';
import { MoreActionsMenu } from '@/components/shared/components/MoreActionsMenu/MoreActionsMenu';
import { ActionsOption } from '@/components/shared/components/MoreActionsMenu/types/ActionsOption';
import { StandardButton, StandardButtonProps } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';
import { useDialog } from '@/components/shared/hooks/useDialog';

export const CheckoutSessionPaymentMethodEditActions = (props: StandardButtonProps) => {
  const {
    showAutoActions,
    showLoanActions,
    loanPaymentMethodDetailEntity,
    checkoutSession,
    isEditVehicleDataDisabled,
    isEditFinancingDetailsDisabled,
    isEditBorrowerDisabled,
    isEditCoBorrowerDisabled,
    isAddCoborrowerDisabled,
    hasCoborrower,
    refreshCheckoutSessionAndPaymentMethodEntities,
    isMerchantPos,
  } = usePaymentMethodEditActions();

  const { opened, setOpened, toggleDialog } = useDialog();
  const [currentAction, setCurrentAction] = useState<PaymentMethodEditAction | undefined>();

  const toggleActionDialog = (actionType: PaymentMethodEditAction) => {
    setCurrentAction(actionType);
    toggleDialog();
  };

  if (!showLoanActions) {
    return null;
  }

  const autoActions: ActionsOption[] = showAutoActions
    ? [
        {
          display: <Text tId="EDIT_VEHICLE_DATA" />,
          onClick: () => toggleActionDialog(AutoLoanPaymentMethodAction.EDIT_VEHICLE_DATA),
          disabled: isEditVehicleDataDisabled,
          iconName: 'directions_car',
        },
        {
          display: <Text tId="EDIT_FINANCING_DETAILS" />,
          onClick: () => toggleActionDialog(AutoLoanPaymentMethodAction.EDIT_FINANCING_DETAILS),
          disabled: isEditFinancingDetailsDisabled,
          iconName: 'account_balance',
          divider: showLoanActions,
        },
      ]
    : [];

  const loanActions: ActionsOption[] = showLoanActions
    ? [
        {
          display: <Text tId="EDIT_BORROWER" />,
          onClick: () => toggleActionDialog(LoanPaymentMethodAction.EDIT_BORROWER),
          disabled: isEditBorrowerDisabled,
          iconName: 'person',
        },
        ...(hasCoborrower
          ? [
              {
                display: <Text tId="EDIT_CO_BORROWER" />,
                onClick: () => toggleActionDialog(LoanPaymentMethodAction.EDIT_CO_BORROWER),
                iconName: 'people',
                disabled: isEditCoBorrowerDisabled,
              },
            ]
          : []),
        ...(!isAddCoborrowerDisabled
          ? [
              {
                display: (
                  <Text
                    tId={
                      isMerchantPos
                        ? loanPaymentMethodDetailEntity?.hasOnboardingDetails(OnboardingDetailsRole.CO_BORROWER)
                          ? 'ADD_GUARANTOR'
                          : 'ADD_CO_BORROWER'
                        : 'ADD_PARTICIPANT'
                    }
                  />
                ),
                onClick: () => toggleActionDialog(LoanPaymentMethodAction.ADD_CO_BORROWER),
                disabled: isAddCoborrowerDisabled,
                iconName: 'group_add',
              },
            ]
          : []),
      ]
    : [];

  const actions: ActionsOption[] = [...autoActions, ...loanActions];
  const disabled = actions.length === 0;

  return (
    <>
      <StandardButton {...props} variant="outlined" size="small" disabled={disabled}>
        <MoreActionsMenu actionsOptionsArray={actions}>
          <Font variant="subtitle2" color={disabled ? 'inherit' : 'textSecondary'}>
            <strong>
              <Text tId="EDIT" />
              <i
                className="material-icons-outlined ml-2"
                style={{ verticalAlign: '-5px', marginLeft: '-4px', fontSize: '1.5em' }}
              >
                edit
              </i>
            </strong>
          </Font>
        </MoreActionsMenu>
      </StandardButton>
      {currentAction && (
        <PaymentMethodEditActionsDialog
          currentAction={currentAction}
          useDialogProps={{ opened, setOpened, toggleDialog }}
          checkoutSession={checkoutSession}
          loanPaymentMethodDetailEntity={loanPaymentMethodDetailEntity}
          refreshCheckoutSessionAndPaymentMethodEntities={refreshCheckoutSessionAndPaymentMethodEntities}
        />
      )}
    </>
  );
};
